import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import menu2Fill from "@iconify/icons-eva/menu-2-fill";
import { styled } from "@mui/material/styles";
import {
  Box,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountPopover from "./AccountPopover";
import NotificationsPopover from "./NotificationsPopover";
import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { get_root_value } from "src/utils/domUtils";
import { useState } from "react";
import {
  markNotificationAsRead,
  notificationList,
} from "src/DAL/Notification/Notification";
import { useNavigate } from "react-router-dom";
import ChatPopover from "src/components/ChatPopover";
import { isGuestMember } from "src/utils/guestMember";

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 42;

const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: "none",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)", // Fix on Mobile
  backgroundColor: get_root_value("--background-primary-color"),
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up("lg")]: {
    minHeight: APPBAR_DESKTOP + 20,
    padding: theme.spacing(0, 4),
  },
}));

DashboardNavbar.propTypes = {
  onOpenSidebar: PropTypes.func,
};

export default function DashboardNavbar({ onOpenSidebar }) {
  const { userInfo, handleUnreadMessageCount, socket } = useContentSetting();
  const navigate = useNavigate();
  const [allNotifications, setAllNotifications] = useState([]);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const notificationIdsRef = useRef(new Set());
  const isGuest = isGuestMember(userInfo);

  const handleRedirect = async (
    notification_id,
    notification_type,
    notification,
  ) => {
    setOpen(false);
    const result = await markNotificationAsRead(notification_id);
    if (result.code === 200) {
      getNotificationList();
      setAllNotifications((notifications) => {
        const notification_list = notifications.map((notification) => {
          if (notification._id === notification_id) {
            return {
              ...notification,
              is_read: true,
            };
          } else {
            return notification;
          }
        });
        return notification_list;
      });

      if (notification_type === "member_left_group") {
        navigate(`/group-feed/${notification.action_id}`);
      } else if (notification_type === "feed_added") {
        navigate(`/community-area-post-detail/${notification.action_id}`);
      } else if (notification_type === "feed_like") {
        navigate(`/community-area-post-detail/${notification.action_id}`);
      } else if (notification_type === "feed_comment_add") {
        navigate(`/community-area-post-detail/${notification.action_id}`);
      } else if (notification_type === "feed_coins_share") {
        navigate(`/community-area-post-detail/${notification.action_id}`);
      } else if (notification_type === "feed_pending") {
        navigate(`/community-area-post-detail/${notification.action_id}`);
      } else if (notification_type === "feed_approved") {
        navigate(`/community-area-post-detail/${notification.action_id}`);
      } else if (notification_type === "feed_rejected") {
        navigate(`/my-posts`, {
          state: { _id: notification.action_id },
        });
      } else if (notification_type === "member_removed_from_group") {
        navigate(`/community-area`);
      } else if (notification_type === "member_added_to_group") {
        navigate(`/community-area`);
      } else if (notification_type === "form_completion_send") {
        navigate(`/forms`);
      } else if (notification_type === "feed_coins_share") {
        navigate(`/community-area-post-detail/${notification.action_id}`);
      } else if (notification_type === "message") {
        navigate(`/chat?chat_id=${notification?.message?.chat_id}`);
      } else if (notification_type === "add_support_ticket") {
        navigate(`/support-ticket`);
      } else if (notification_type === "friend_request") {
        navigate(`/chat/friend-list`, {
          state: {
            tabValue: 2,
          },
        });
      } else if (notification_type === "friend_request_rejection") {
        navigate(`/chat/friend-list`);
      } else if (notification_type === "friend_request_acceptance") {
        navigate(`/chat/friend-list`, {
          state: {
            tabValue: 0,
          },
        });
      } else if (notification_type === "accountability_partner_request") {
        navigate(`/chat/friend-list`, {
          state: {
            tabValue: 3,
          },
        });
      } else if (notification_type === "accountability_request_acceptance") {
        navigate(`/chat/friend-list`, {
          state: {
            tabValue: 4,
          },
        });
      } else if (notification_type === "friend_request_rejection") {
        navigate(`/chat/friend-list`, {
          state: {
            tabValue: 0,
          },
        });
      } else if (notification_type === "form_feedback") {
        navigate(`/forms-Details/${notification.form_slug}/${userInfo._id}`);
      } else if (notification_type === "form_feedback_edit") {
        navigate(`/forms-Details/${notification.form_slug}/${userInfo._id}`);
      } else if (notification_type === "workout_comment_added") {
        const memberId = notification?.member;
        const programId = notification?.program;
        const workoutId = notification?.workout;
        if (memberId && programId && workoutId) {
          navigate(`/workout-edit/${memberId}/${programId}/${workoutId}`);
        } else {
          enqueueSnackbar("Invalid notification data for workout.", {
            variant: "error",
          });
        }
      } else if (notification_type === "deficit_off_notification") {
        navigate(`/edit-profile`);
      } else if (notification_type === "WEIGHT_REMINDER") {
        navigate(`/weight-tracker`);
      }
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const getNotificationList = async () => {
    const result = await notificationList();
    if (result.code === 200) {
      setAllNotifications(result.notifications);
      handleUnreadMessageCount(result?.unread_chat_counr);
      setTotalNotifications(result.total_notifications_count);
      setUnreadNotificationCount(result.unread_notifications);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  useEffect(() => {
    if (isGuest) return;
    getNotificationList();
  }, [isGuest]);

  useEffect(() => {
    notificationIdsRef.current = new Set(allNotifications.map((n) => n?._id));
  }, [allNotifications]);

  // Real-time admin "General Notifications" (backend emits to the member's user-id room).
  useEffect(() => {
    if (isGuest || !socket?.on) return;

    const handleMemberNotification = (data) => {
      const notification = data?.notification;
      if (!notification?._id) return;
      if (notificationIdsRef.current.has(notification._id)) return;
      notificationIdsRef.current = new Set([
        ...notificationIdsRef.current,
        notification._id,
      ]);

      const newNotification = {
        ...notification,
        is_read: false,
        createdAt: notification.createdAt || new Date().toISOString(),
      };
      setAllNotifications((notifications) =>
        notifications.some((item) => item?._id === newNotification._id)
          ? notifications
          : [newNotification, ...notifications],
      );
      setUnreadNotificationCount((count) => (Number(count) || 0) + 1);

      enqueueSnackbar(newNotification.title || "New notification", {
        variant: "info",
        action: (key) => (
          <>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                closeSnackbar(key);
                setOpen(true);
              }}
            >
              View
            </Button>
            <Button
              className="snackbar-cross-icon"
              onClick={() => closeSnackbar(key)}
            >
              <CloseIcon />
            </Button>
          </>
        ),
      });
    };

    socket.on("member_notification_receiver", handleMemberNotification);
    return () => {
      socket.off("member_notification_receiver", handleMemberNotification);
    };
  }, [socket, isGuest, enqueueSnackbar, closeSnackbar]);

  return (
    <RootStyle>
      <ToolbarStyle sx={{ background: "#fff" }}>
        <IconButton
          onClick={onOpenSidebar}
          sx={{ mr: 1, color: "text.primary", marginRight: "auto" }}
        >
          <Icon icon={menu2Fill} />
        </IconButton>
        <Box sx={{ flexGrow: 1 }} />
        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: 0.5, sm: 0.7 }}
        >
          {isGuest ? null : (
            <>
              <ChatPopover />
              <NotificationsPopover
                totalNotifications={totalNotifications}
                allNotifications={allNotifications}
                unreadNotificationCount={unreadNotificationCount}
                setUnreadNotificationCount={setUnreadNotificationCount}
                setAllNotifications={setAllNotifications}
                handleRedirect={handleRedirect}
                setOpen={setOpen}
                open={open}
              />
            </>
          )}
          <AccountPopover />
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}
